import React, { useEffect, useRef, useState } from 'react';
import { withAlert } from 'react-alert';
import { compose } from 'redux';
import { Button, Col, Form, Input, Row, Select, Divider, DatePicker, Card, Typography, AutoComplete, TimePicker, Empty, Radio, Tooltip, Descriptions, Upload, Modal, message } from 'antd';
import { CarOutlined, CloseCircleOutlined, CloseOutlined, PlusOutlined, HeartOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { useJsApiLoader, GoogleMap, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import Loading from '../../../../components/loading';
import GeneralHelper from '../../../../helpers/general';
import LocalStorageHelper from '../../../../helpers/local_storage';
import CommonEnum from '../../../../constants/CommonEnum';

import LabelHelper from '../../../../helpers/Label';
import FileHelper from '../../../../helpers/file';
import config from '../../../../config';
import ApplicationHelper from '../../../../helpers/application';
import ModalMap from './ModalMap';

const { Item } = Form;
const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

dayjs.extend(duration);
dayjs.extend(isSameOrBefore);

const BookingApplicationView = props => {
	const { cancelApplicationTripById, cancelAllTrips, pageProps } = props;
	const { application, hkust_points, favorite_destinations, staffs, previous_bookings, loading, loadingStaffs, loadingSubmit, loadingStatus, loadingFavoriteLocationSubmit, loadingCancel } = pageProps || {};
	const {
		custom_id,
		booker_id,
		booker_department,
		booker_email,
		booker_name,
		contact_person_mobile,
		contact_person_name,
		booking_managers,
		date_of_trip,
		trip_country,
		guest_staff_email,
		guest_staff_name,
		guest_staff_title,
		guest_type,
		guest_vip_name,
		guest_vip_title,
		need_welcab,
		purpose_of_trip,
		is_draft
	} = application || {};
	const booker = LocalStorageHelper.getBooker();
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: config.google_api_key,
		libraries: ['places']
	});

	const [form1] = Form.useForm();
	const [form2] = Form.useForm();
	const [modalApi, modalContextHolder] = Modal.useModal();

	// Snapshot of the form values captured right after the application is loaded into the
	// forms, used to detect whether the user has actually changed anything before allowing
	// "Update and Save". useWatch re-renders this component on any form change (including
	// programmatic setFieldsValue from address selection, etc.) so the dirty check stays live.
	const initialFormSnapshotRef = useRef({ form1: {}, form2: {} });
	// Baseline of uploaded/attached documents captured alongside the form snapshot above,
	// so document-only edits (add/remove a file) also register as a change.
	const initialFileListRef = useRef([]);
	// Tracks the exact `application` object the forms were last populated from. Redux only swaps
	// in a new object when fresh server data arrives (the initial fetch, and the refetch that
	// follows a save / trip cancellation), while the other dependencies of the load effect below
	// (hkust_points/favorite_destinations/staffs/previous_bookings/isLoaded) resolve without
	// touching it. Comparing by reference therefore keeps late-arriving async data from stomping
	// edits in progress while still re-syncing the forms once the server answers - which is what
	// puts the server-assigned `application_trip_id` onto each newly created trip. Without that
	// resync the next save posts every trip without an id and the API stores them as new trips,
	// duplicating the whole booking.
	const populatedApplicationRef = useRef(null);
	// Tracks whether a staffs fetch cycle has actually completed (as opposed to `loadingStaffs`
	// simply being false because the fetch hasn't started yet), so the load effect below can wait
	// for real staff data before looking up guest_staff_name/title via handleEmailOnSelect.
	const staffsFetchStartedRef = useRef(false);
	useEffect(() => {
		if (loadingStaffs) staffsFetchStartedRef.current = true;
	}, [loadingStaffs]);
	Form.useWatch([], form1);
	Form.useWatch([], form2);

	// states
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [selectedGuestType, setSelectedGuestType] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false); // modal for map
	const [canBookChinaTrip, setCanBookChinaTrip] = useState(false);
	const [selectedPickupType, setSelectedPickupType] = useState(['search_location']); //	values: search_location, inside_hkust, favorite_destination
	const [selectedDropoffType, setSelectedDropoffType] = useState(['search_location']); //	values: search_location, inside_hkust, favorite_destination
	const [dateOfTrip, setDateOfTrip] = useState(null);
	const [selectedPurposeOfTrip, setSelectedPurposeOfTrip] = useState('');
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewDocument, setPreviewDocument] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [fileList, setFileList] = useState([]);

	//	outside hkust states
	const [routeType, setRouteType] = useState(null); //	used when open modal
	const [activeIndex, setActiveIndex] = useState(null); //	Form trip index when opening modal
	const [tripRoutes, setTripRoutes] = useState([]);
	const [origin, setOrigin] = useState([]); //	map origin	// to be deleted
	const [destination, setDestination] = useState([]); //	map destination 	// to be deleted

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		props.getHkustPoints();
		props.getFavoriteDestinations();
		props.getStaffs();
		props.getApplicationPreviousBookings();

		// Add event listener for window resize
		window.addEventListener('resize', handleResize);

		// Cleanup the event listener on component unmount
		return () => {
			window.removeEventListener('resize', handleResize);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const { trips } = application || {};
		if (application) {
			// This effect also reruns whenever hkust_points/favorite_destinations/staffs/
			// previous_bookings/isLoaded resolve asynchronously (they're populated by network
			// calls fired on mount). Those never replace `application`, so only (re)populate the
			// forms when the `application` object itself is a payload we haven't loaded yet - a
			// late-arriving async dependency then can't wipe out edits the user has made, while a
			// freshly saved application is still pulled back into the forms.
			const isNewApplicationPayload = populatedApplicationRef.current !== application;
			// createApplication / updateApplicationById / the cancel endpoints answer with a bare
			// application document (no `trips`, no `assets`); the complete record follows a moment
			// later from getApplicationById, which always sets `assets`. Waiting for that payload
			// keeps the intermediate response from blanking the attachments or leaving the trips
			// out of sync with the server.
			const isDetailedApplication = Array.isArray(application.assets);
			// guest_staff_name/title below are looked up from `staffs` via handleEmailOnSelect,
			// so the population must wait for a real (non-empty-by-default) staffs fetch, not just
			// the initial `loadingStaffs === false` before the fetch even starts.
			const staffsReady = staffsFetchStartedRef.current && !loadingStaffs;
			const shouldPopulateForms = isNewApplicationPayload && isDetailedApplication && staffsReady;

			if (shouldPopulateForms) {
				form1.setFieldsValue({
					booker_department: booker_department ? booker_department : null,
					booker_email: booker_email ? booker_email : null,
					booker_name: booker_name ? booker_name : null,
					contact_person_mobile: contact_person_mobile ? contact_person_mobile : null,
					contact_person_name: contact_person_name ? contact_person_name : null,
					booking_managers: booking_managers && booking_managers.length > 0 ? booking_managers : [],
					date_of_trip: date_of_trip ? dayjs(date_of_trip) : null,
					trip_country: trip_country ? trip_country : null,
					guest_staff_email: guest_staff_email ? guest_staff_email : null,
					guest_staff_name: guest_staff_name ? guest_staff_name : null,
					guest_staff_title: guest_staff_title ? guest_staff_title : null,
					guest_type: guest_type ? guest_type : null,
					guest_vip_name: guest_vip_name ? guest_vip_name : null,
					guest_vip_title: guest_vip_title ? guest_vip_title : null,
					need_welcab: !_.isNil(need_welcab) ? need_welcab : null,
					purpose_of_trip: purpose_of_trip ? purpose_of_trip : null
				});

				initialFileList();

				if (!contact_person_name && !contact_person_mobile && !guest_type && !guest_staff_email && !guest_staff_name && !guest_staff_title && !guest_vip_name && !guest_vip_title && !need_welcab) {
					if (previous_bookings[0]) {
						form1.setFieldsValue({
							contact_person_name: previous_bookings[0].contact_person_name,
							contact_person_mobile: previous_bookings[0].contact_person_mobile,
							guest_staff_email: previous_bookings[0].guest_staff_email,
							guest_staff_name: previous_bookings[0].guest_staff_name,
							guest_staff_title: previous_bookings[0].guest_staff_title,
							guest_type: previous_bookings[0].guest_type,
							guest_vip_name: previous_bookings[0].guest_vip_name,
							guest_vip_title: previous_bookings[0].guest_vip_title,
							need_welcab: previous_bookings[0].need_welcab
						});
						if (previous_bookings[0].guest_type) {
							setSelectedGuestType(previous_bookings[0].guest_type);
						}

						handleEmailOnSelect(previous_bookings[0].guest_staff_email);
					}

				} else {
					setSelectedGuestType(guest_type);
					handleEmailOnSelect(guest_staff_email);
				}

				setDateOfTrip(date_of_trip ? dayjs(date_of_trip) : null);
				setSelectedPurposeOfTrip(purpose_of_trip);
			}
			if (trips && trips.length > 0) {
				const formattedTrips = trips.map(trip => ({
					...trip,
					starting_pickup_type: trip.inside_hkust_pickup ? 'inside_hkust' : trip.favorite_destination_pickup ? 'favorite_destination' : 'search_location',
					drop_off_type: trip.inside_hkust_drop_off ? 'inside_hkust' : trip.favorite_destination_drop_off ? 'favorite_destination' : 'search_location',
					application_trip_id: trip.application_trip_id,
					time_of_departure: trip.time_of_departure ? dayjs(trip.time_of_departure) : null,
					remark: trip.remark
				}));

				const tempTripRoutes = [];
				// new approach for displaying starting address and destination address
				const tempSelectedPickupType = [];
				const tempSelectedDropoffType = [];
				// end of new approach
				trips.map((trip, index) => {
					const origin = [];
					const destination = [];
					if (trip.inside_hkust_pickup === true) {
						tempSelectedPickupType.push('inside_hkust'); //	new approach
						if (trip.pickup_address_routes_hkust && trip.pickup_address_routes_hkust.length > 0) {
							trip.pickup_address_routes_hkust.map(pickup => {
								const selectedHkustPoint = hkust_points.find(point => point._id === pickup);

								let hkustPoint;
								if (selectedHkustPoint) {
									hkustPoint = {
										lat: parseFloat(selectedHkustPoint.latitude),
										lng: parseFloat(selectedHkustPoint.longitude)
									};
								}
								hkustPoint && origin.push(hkustPoint);
							});
						}
					} else if (trip.favorite_destination_pickup === true) {
						tempSelectedPickupType.push('favorite_destination'); //	new approach
						if (trip.pickup_address_routes_favorite && trip.pickup_address_routes_favorite.length > 0) {
							trip.pickup_address_routes_favorite.map(pickup => {
								const selectedFavoriteDestination = favorite_destinations.find(favorite_destination => favorite_destination._id === pickup);
								let favoriteDestination;
								if (selectedFavoriteDestination) {
									favoriteDestination = {
										lat: parseFloat(selectedFavoriteDestination.latitude),
										lng: parseFloat(selectedFavoriteDestination.longitude)
									};
								}

								favoriteDestination && origin.push(favoriteDestination);
							});
						}
					} else {
						tempSelectedPickupType.push('search_location'); //	new approach
						if (trip.pinned_pickup_latitude && trip.pinned_pickup_longitude) {
							origin.push({
								lat: parseFloat(trip.pinned_pickup_latitude),
								lng: parseFloat(trip.pinned_pickup_longitude)
							});
						}
					}

					if (trip.inside_hkust_drop_off === true) {
						tempSelectedDropoffType.push('inside_hkust'); //	new approach
						if (trip.dropoff_address_routes_hkust && trip.dropoff_address_routes_hkust.length > 0) {
							trip.dropoff_address_routes_hkust.map(dropOff => {
								const selectedHkustPoint = hkust_points.find(point => point._id === dropOff);

								let hkustPoint;
								if (selectedHkustPoint) {
									hkustPoint = {
										lat: parseFloat(selectedHkustPoint.latitude),
										lng: parseFloat(selectedHkustPoint.longitude)
									};
								}
								hkustPoint && destination.push(hkustPoint);
							});
						}
					} else if (trip.favorite_destination_drop_off === true) {
						tempSelectedDropoffType.push('favorite_destination'); //	new approach
						if (trip.dropoff_address_routes_favorite && trip.dropoff_address_routes_favorite.length > 0) {
							trip.dropoff_address_routes_favorite.map(dropOff => {
								const selectedFavoriteDestination = favorite_destinations.find(favorite_destination => favorite_destination._id === dropOff);
								let favoriteDestination;
								if (selectedFavoriteDestination) {
									favoriteDestination = {
										lat: parseFloat(selectedFavoriteDestination.latitude),
										lng: parseFloat(selectedFavoriteDestination.longitude)
									};
								}

								favoriteDestination && destination.push(favoriteDestination);
							});
						}
					} else {
						tempSelectedDropoffType.push('search_location'); //	new approach
						if (trip.pinned_drop_off_latitude && trip.pinned_drop_off_longitude) {
							destination.push({
								lat: parseFloat(trip.pinned_drop_off_latitude),
								lng: parseFloat(trip.pinned_drop_off_longitude)
							});
						}
					}

					tempTripRoutes.push({
						origin,
						destination
					});
				});

				if (shouldPopulateForms) {
					form2.setFieldsValue({ trips: formattedTrips });
				}

				// new approach
				setSelectedPickupType(tempSelectedPickupType);
				setSelectedDropoffType(tempSelectedDropoffType);
				isLoaded && handleDirections(tempTripRoutes);
			}
			if (shouldPopulateForms) {
				// Capture the freshly loaded values as the baseline for the dirty check.
				initialFormSnapshotRef.current = {
					form1: form1.getFieldsValue(true),
					form2: form2.getFieldsValue(true)
				};
				populatedApplicationRef.current = application;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [application, hkust_points, favorite_destinations, form2, isLoaded, previous_bookings, staffs, loadingStaffs]);

	/***************** START OF FUNCTIONS *****************/

	const buildTripSnapshot = trip => {
		const pickupType = trip.starting_pickup_type
			? trip.starting_pickup_type
			: trip.inside_hkust_pickup ? 'inside_hkust' : trip.favorite_destination_pickup ? 'favorite_destination' : 'search_location';
		const dropOffType = trip.drop_off_type
			? trip.drop_off_type
			: trip.inside_hkust_drop_off ? 'inside_hkust' : trip.favorite_destination_drop_off ? 'favorite_destination' : 'search_location';
		const pickup = pickupType === 'inside_hkust'
			? { routes: [...(trip.pickup_address_routes_hkust || [])].sort() }
			: pickupType === 'favorite_destination'
				? { routes: [...(trip.pickup_address_routes_favorite || [])].sort() }
				: { name: trip.pinned_pickup_location_name || null, address: trip.pinned_pickup_address || null };
		const dropOff = dropOffType === 'inside_hkust'
			? { routes: [...(trip.dropoff_address_routes_hkust || [])].sort() }
			: dropOffType === 'favorite_destination'
				? { routes: [...(trip.dropoff_address_routes_favorite || [])].sort() }
				: { name: trip.pinned_drop_off_location_name || null, address: trip.pinned_drop_off_address || null };
		return {
			time_of_departure: trip.time_of_departure ? dayjs(trip.time_of_departure).valueOf() : null,
			passengers: !_.isNil(trip.passengers) ? trip.passengers : null,
			remark: trip.remark || '',
			pickupType,
			pickup,
			dropOffType,
			dropOff
		};
	};

	const submitApplication = async () => {
		try {
			const form1Values = await form1.validateFields();
			const form2Values = await form2.validateFields();

			let assets = [];
			if (fileList.length > 0) {
				const updatedFileList = fileList && fileList.length > 0 &&
				await Promise.all(fileList.map(async (asset, index) => {
					if (asset.originFileObj) {
						const fileContent = await FileHelper.fileToDataUrl(asset.originFileObj);
						const fileNameParts = asset.name.split('.');
						const fileExtension = asset.extension || fileNameParts.pop();
						return {
							...asset,
							file_content: fileContent,
							file_extension: fileExtension,
							file_name: asset.name,
							file_type: asset.type
						};
					} else {
						const fileNameParts = asset.name ? asset.name.split('.') : asset.file_name.split('.');
						const fileExtension = asset.extension || fileNameParts.pop();
						return {
							...asset,
							file_content: asset.file || asset.url,
							file_extension: fileExtension,
							file_name: asset.name ? asset.name : asset.file_name,
							file_type: asset.type
						};
					}
				}));
				assets = updatedFileList;
			}

			props.onSubmit({
				...form1Values,
				need_welcab: booker && booker.is_welcab === 'yes' ? form1Values.need_welcab : false,
				assets,
				trips:
					form2Values.trips &&
					form2Values.trips.map((trip, index) => ({
						...trip,
						inside_hkust_pickup: trip.starting_pickup_type === 'inside_hkust' ? true : false,
						inside_hkust_drop_off: trip.drop_off_type === 'inside_hkust' ? true : false,
						favorite_destination_pickup: trip.starting_pickup_type === 'favorite_destination' ? true : false,
						favorite_destination_drop_off: trip.drop_off_type === 'favorite_destination' ? true : false,
						...(trip.starting_pickup_type === 'search_location' &&
							tripRoutes[index] &&
							tripRoutes[index].origin &&
							tripRoutes[index].origin[0] && { pinned_pickup_latitude: tripRoutes[index].origin[0].lat, pinned_pickup_longitude: tripRoutes[index].origin[0].lng }),
						...(trip.drop_off_type === 'search_location' &&
							tripRoutes[index] &&
							tripRoutes[index].destination &&
							tripRoutes[index].destination[0] && { pinned_drop_off_latitude: tripRoutes[index].destination[0].lat, pinned_drop_off_longitude: tripRoutes[index].destination[0].lng })
					}))
			});
		} catch (error) {
			console.log('Validation failed:', error);
			window.alert('Some required fields are missing. Please review the form and complete all fields.');
		}
	};

	const isBackdatedBooking = () => {
		const currentDateOfTrip = form1.getFieldValue('date_of_trip');
		return !!currentDateOfTrip && dayjs(currentDateOfTrip).startOf('day').isBefore(dayjs().startOf('day'));
	};

	const confirmBackdatedBooking = () => {
		modalApi.confirm({
			title: 'This is a backdated booking!',
			icon: <ExclamationCircleFilled style={{ color: '#FAB328' }} />,
			content: 'Are you sure to submit this application?',
			okText: 'Yes',
			cancelText: 'No',
			onOk: submitApplication
		});
	};

	const handleSaveButtonClicked = () => {
		if (props.step === 1) {
			if (isBackdatedBooking()) {
				confirmBackdatedBooking();
				return;
			}
			if (window.confirm('Are you sure you want to submit this application?')) {
				submitApplication();
			}
			return;
		}

		if (isBackdatedBooking()) {
			confirmBackdatedBooking();
			return;
		}

		const currentTrips = (form2.getFieldsValue(true) || {}).trips || [];
		const originalTrips = (application && application.trips) || [];
		// Modal 1 warns that an approved trip's time, starting point, or destination
		// changed, which may require re-approval and move it back to "Pending".
		const hasApprovedTripKeyFieldsChanged = currentTrips.some((trip, index) => {
			const original = trip.application_trip_id
				? originalTrips.find(t => t.application_trip_id === trip.application_trip_id)
				: originalTrips[index];
			if (!original || original.status !== 'approved') {
				return false;
			}
			const currentSnapshot = buildTripSnapshot(trip);
			const originalSnapshot = buildTripSnapshot(original);
			return currentSnapshot.time_of_departure !== originalSnapshot.time_of_departure
				|| currentSnapshot.pickupType !== originalSnapshot.pickupType
				|| !_.isEqual(currentSnapshot.pickup, originalSnapshot.pickup)
				|| currentSnapshot.dropOffType !== originalSnapshot.dropOffType
				|| !_.isEqual(currentSnapshot.dropOff, originalSnapshot.dropOff);
		});
		// Changing the shared trip date shifts every trip's departure time on the server too,
		// so it carries the same risk for any approved trip.
		const hasApprovedTrip = originalTrips.some(trip => trip.status === 'approved');
		const currentDateOfTrip = form1.getFieldValue('date_of_trip');
		const isDateOfTripChanged = !!(application && application.date_of_trip) && !!currentDateOfTrip
			&& dayjs(currentDateOfTrip).format('YYYY-MM-DD') !== dayjs(application.date_of_trip).format('YYYY-MM-DD');
		const keyFieldsChangedOnApprovedTrip = hasApprovedTripKeyFieldsChanged || (hasApprovedTrip && isDateOfTripChanged);

		if (keyFieldsChangedOnApprovedTrip) {
			modalApi.confirm({
				title: 'Confirm Trip Update',
				icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
				content: <>You&apos;ve changed <b>the time, starting point or destination</b>. This may cause the trip to require re-approval and become <b>Pending</b>, and the status <b>should be auto-confirmed on the availability</b>. Do you want to continue? <br/><br/> <span style={{ color: '#c30010' }}>Please contact the driver directly to make any ad hoc changes.</span></>,
				okText: 'Agree and Update',
				cancelText: 'No',
				onOk: submitApplication
			});
			return;
		}

		// Anything else worth confirming - non-time edits to an approved trip, or any edit
		// at all to a pending trip - gets the plain confirmation instead.
		if (isFormDirty) {
			modalApi.confirm({
				title: 'Confirm Trip Update',
				icon: <ExclamationCircleFilled style={{ color: '#FAB328' }} />,
				content: 'Are you sure you want to update this trip?',
				okText: 'Yes',
				cancelText: 'No',
				onOk: submitApplication
			});
			return;
		}

		submitApplication();
	};

	const handleCancelClick = async (type, applicationTripId) => {
		try {
			const form1Values = await form1.validateFields();
			const form2Values = await form2.validateFields();

			let assets = [];
			if (fileList.length > 0) {
				const updatedFileList = fileList && fileList.length > 0 &&
				await Promise.all(fileList.map(async (asset, index) => {
					if (asset.originFileObj) {
						const fileContent = await FileHelper.fileToDataUrl(asset.originFileObj);
						const fileNameParts = asset.name.split('.');
						const fileExtension = asset.extension || fileNameParts.pop();
						return {
							...asset,
							file_content: fileContent,
							file_extension: fileExtension,
							file_name: asset.name,
							file_type: asset.type
						};
					} else {
						const fileNameParts = asset.name ? asset.name.split('.') : asset.file_name.split('.');
						const fileExtension = asset.extension || fileNameParts.pop();
						return {
							...asset,
							file_content: asset.file || asset.url,
							file_extension: fileExtension,
							file_name: asset.name ? asset.name : asset.file_name,
							file_type: asset.type
						};
					}
				}));
				assets = updatedFileList;
			}

			if (type === 'single') {
				cancelApplicationTripById(applicationTripId, {
					...form1Values,
					need_welcab: booker && booker.is_welcab === 'yes' ? form1Values.need_welcab : false,
					assets,
					trips:
						form2Values.trips &&
						form2Values.trips.map((trip, index) => ({
							...trip,
							inside_hkust_pickup: trip.starting_pickup_type === 'inside_hkust' ? true : false,
							inside_hkust_drop_off: trip.drop_off_type === 'inside_hkust' ? true : false,
							favorite_destination_pickup: trip.starting_pickup_type === 'favorite_destination' ? true : false,
							favorite_destination_drop_off: trip.drop_off_type === 'favorite_destination' ? true : false,
							...(trip.starting_pickup_type === 'search_location' &&
								tripRoutes[index] &&
								tripRoutes[index].origin &&
								tripRoutes[index].origin[0] && { pinned_pickup_latitude: tripRoutes[index].origin[0].lat, pinned_pickup_longitude: tripRoutes[index].origin[0].lng }),
							...(trip.drop_off_type === 'search_location' &&
								tripRoutes[index] &&
								tripRoutes[index].destination &&
								tripRoutes[index].destination[0] && { pinned_drop_off_latitude: tripRoutes[index].destination[0].lat, pinned_drop_off_longitude: tripRoutes[index].destination[0].lng })
						}))
				});
			} else if (type === 'all') {
				cancelAllTrips({
					...form1Values,
					need_welcab: booker && booker.is_welcab === 'yes' ? form1Values.need_welcab : false,
					assets,
					trips:
						form2Values.trips &&
						form2Values.trips.map((trip, index) => ({
							...trip,
							inside_hkust_pickup: trip.starting_pickup_type === 'inside_hkust' ? true : false,
							inside_hkust_drop_off: trip.drop_off_type === 'inside_hkust' ? true : false,
							favorite_destination_pickup: trip.starting_pickup_type === 'favorite_destination' ? true : false,
							favorite_destination_drop_off: trip.drop_off_type === 'favorite_destination' ? true : false,
							...(trip.starting_pickup_type === 'search_location' &&
								tripRoutes[index] &&
								tripRoutes[index].origin &&
								tripRoutes[index].origin[0] && { pinned_pickup_latitude: tripRoutes[index].origin[0].lat, pinned_pickup_longitude: tripRoutes[index].origin[0].lng }),
							...(trip.drop_off_type === 'search_location' &&
								tripRoutes[index] &&
								tripRoutes[index].destination &&
								tripRoutes[index].destination[0] && { pinned_drop_off_latitude: tripRoutes[index].destination[0].lat, pinned_drop_off_longitude: tripRoutes[index].destination[0].lng })
						}))
				});
			}
		} catch (error) {
			console.log('Validation failed:', error);
			window.alert('Some required fields are missing. Please review the form and complete all fields.');
		}
	};

	const handleDirections = async trips => {
		const newTripRoutes = [...trips];
		const dateOfTrip = form1.getFieldValue('date_of_trip');

		//	Process directions only when is_draft is false
		await Promise.all(
			trips.map(async (trip, index) => {
				if (trip.origin && trip.origin.length > 0 && trip.destination && trip.destination.length > 0) {
					const timeOfDeparture = form2.getFieldValue(['trips', index, 'time_of_departure']);
					if (trip.origin.length > 0 && trip.destination.length > 0 && dateOfTrip && timeOfDeparture) {
						const directionsService = new window.google.maps.DirectionsService();

						const newDateOfTrip = dayjs(dateOfTrip, 'YYYY-MM-DD');
						const newDepartureTime = dayjs(timeOfDeparture, 'HH:mm');

						const departureTime = newDateOfTrip
							.hour(newDepartureTime.hour())
							.minute(newDepartureTime.minute())
							.second(0)
							.millisecond(0)
							.toDate();

						let googleDepartureTime;
						if (dayjs(departureTime).isSameOrBefore(dayjs())) {
							googleDepartureTime = dayjs()
								.hour(newDepartureTime.hour())
								.minute(newDepartureTime.minute())
								.second(0)
								.millisecond(0)
								.add(1, 'day')
								.toDate();
						} else {
							googleDepartureTime = departureTime;
						}
						return new Promise(resolve => {
							directionsService.route(
								{
									origin: trip.origin[0],
									destination: trip.destination[trip.destination.length - 1],
									waypoints: [...trip.origin.slice(1).map(location => ({ location, stopover: true })), ...trip.destination.slice(0, -1).map(location => ({ location, stopover: true }))],
									travelMode: window.google.maps.TravelMode.DRIVING,
									drivingOptions: {
										departureTime: googleDepartureTime, //new Date(Date.now()),
										trafficModel: 'pessimistic'	// bestguess = most useful predictions for the vast majority of use cases, pessimistic = bad traffic days, optimistic = good traffic days
									}
								},
								(result, status) => {
									if (status === window.google.maps.DirectionsStatus.OK) {
										let totalDistance = 0;
										let totalDuration = 0;

										result.routes[0].legs.forEach(leg => {
											totalDistance += leg.distance.value;
											totalDuration += leg.duration_in_traffic
												? leg.duration_in_traffic.value // Traffic-aware duration
												: leg.duration.value; // Default duration if no traffic data
										});

										// Convert total distance and duration to readable format (text)
										const formattedDistance = (totalDistance / 1000).toFixed(2) + ' km';

										const estimatedArrivalDateTime = dayjs(departureTime).add(dayjs.duration(totalDuration, 'seconds'));
										const estimatedArrivalTime = estimatedArrivalDateTime.format('HH:mm');

										form2.setFieldsValue({ trips: { [index]: { estimated_time_arrival_no_buffer: estimatedArrivalDateTime } } });

										// Calculate midpoint of the route using geometry distance
										const route = result.routes[0].legs.reduce((acc, leg) => acc.concat(leg.steps), []);
										const totalDistanceMeters = route.reduce((acc, step) => acc + step.distance.value, 0);
										let accumulatedDistance = 0;

										// Find the step closest to half of the total distance
										let midpointStep;
										route.some((step, index) => {
											accumulatedDistance += step.distance.value;
											if (accumulatedDistance >= totalDistanceMeters / 2) {
												midpointStep = step;
												return true;
											}
											return false;
										});

										const midpoint = {
											lat: midpointStep.start_location.lat(),
											lng: midpointStep.start_location.lng()
										};

										newTripRoutes[index] = {
											...newTripRoutes[index],
											directions: result,
											hasError: false,
											infoWindows: [
												{
													position: midpoint,
													trafficDuration: estimatedArrivalTime, //formattedDuration,
													distance: formattedDistance
												}
											]
										};

										resolve();
									} else if (status === window.google.maps.DirectionsStatus.ZERO_RESULTS) {
										// const newTripRoutes = [...tripRoutes];
										newTripRoutes[index] = {
											...newTripRoutes[index],
											directions: null,
											hasError: true
										};
										props.alert.show('No route could be found.', { type: 'error' });

										resolve();
									} else {
										// const newTripRoutes = [...tripRoutes];
										if (routeType === 'origin') {
											newTripRoutes[index] = {
												...newTripRoutes[index],
												origin: []
											};
											form2.setFieldsValue({ trips: { [index]: { pinned_pickup_address: null } } });
											form2.setFieldsValue({ trips: { [index]: { pinned_pickup_location_name: null } } });
										} else {
											newTripRoutes[index] = {
												...newTripRoutes[index],
												destination: []
											};
											form2.setFieldsValue({ trips: { [index]: { pinned_drop_off_address: null } } });
											form2.setFieldsValue({ trips: { [index]: { pinned_drop_off_location_name: null } } });
										}
										newTripRoutes[index] = {
											...newTripRoutes[index],
											directions: null,
											hasError: true
										};

										console.error(`Directions request failed due to ${status}`);
										resolve();
									}
								}
							);
						});
					}
				}
			})
		);

		//	updating Trip Routes State
		const updatedTripRoutes = [...newTripRoutes];
		setTripRoutes(updatedTripRoutes);
	};

	const handleGetAddress = (address, currentMarked, map) => {
		if (address || currentMarked) {
			const { lat, lng } = currentMarked;
			const latLng = new window.google.maps.LatLng(lat, lng);
			const geocoder = new window.google.maps.Geocoder();

			geocoder.geocode({ ...address ? { address } : { location: latLng } }, (results, status) => {
				if (status === 'OK' && results[0]) {

					const coordinates = {
						lat: results[0].geometry.location.lat(),
						lng: results[0].geometry.location.lng()
					};

					const placeName = (address.split(',') || []).length > 3 ? `${address.split(',')[0].trim()}, ${address.split(',')[1].trim()}` : address.split(',')[0].trim();
					const formattedAddress = address;

					form2.setFieldsValue({
						trips: {
							[activeIndex]: {
								...(routeType === 'origin'
									? {
										pinned_pickup_location_name: placeName.trim() ? placeName.trim() : formattedAddress.trim(),
										pinned_pickup_address: formattedAddress.trim(),
										origin: {
											...coordinates
										}
									}
									: {
										pinned_drop_off_location_name: placeName.trim() ? placeName.trim() : formattedAddress.trim(),
										pinned_drop_off_address: formattedAddress.trim(),
										destination: {
											...coordinates
										}
									}
								)
							}
						}
					});
				} else {
					props.alert.show(`Failed to get address: ${status}`, { type: 'error' });
				}
			});
		} else {
			form2.setFieldsValue({
				trips: {
					[activeIndex]: {
						pinned_pickup_location_name: null,
						pinned_pickup_address: null,
						...routeType === 'origin'
							? { origin: {} }
							: { destination: {} }
					}
				}
			});
		}
	};

	const handleEmailOnSelect = value => {
		const staff = staffs ? staffs.find(staff => staff.email === value) : null;
		form1.setFieldsValue({
			guest_staff_name: staff ? staff.name : null,
			guest_staff_title: staff ? staff.title : null,
			...!staff || !staff.can_book_china_trip && { trip_country: 'hongkong' }
		});
		setCanBookChinaTrip(staff && staff.can_book_china_trip);

		const { trips } = form2.getFieldsValue();
		const newTrips = trips && trips.length > 0 && trips.map(trip => {
			const { passengers: newPassengers } = trip;
			newPassengers[0] = staff ? staff.name : null;
			return {
				...trip,
				passengers: newPassengers
			};
		});
		form2.setFieldsValue({ trips: newTrips });
	};

	const emailRules = [
		{ required: true, message: 'Please enter email.' },
		{
			pattern: new RegExp(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/),
			message: 'Invalid Email'
		},
		{
			validator: (_, value) => {
				if (GeneralHelper.isUstEmail(value)) {
					return Promise.resolve();
				}
				return Promise.reject('Please enter a valid ITSC email ending with @ust.hk');
			}
		}
	];

	const vipGuestNameRules = [
		{ required: true, message: 'Please enter vip guest name.' },
		// { pattern: new RegExp(/^[A-Z a-z]+$/), message: 'Invalid Name' },
		{
			validator: (_, value) => {
				if (!value || value.trim() === '') {
					return Promise.reject('VIP Name cannot be empty or contain only spaces');
				}
				return Promise.resolve();
			}
		}
	];

	const validatePhone = (_, value) => {
		const phonePattern = /^(\+)?\d+$/;
		if (value && !phonePattern.test(value)) {
			return Promise.reject('Invalid phone number');
		}

		if (!value) {
			return Promise.reject('Please enter contact person mobile.');
		}
		return Promise.resolve();
	};

	const disabledDate = current => {
		// Define today's date and the maximum selectable date
		const today = dayjs().startOf('day');

		const maxDate = today.add(booker.advance_booking_days, 'day').endOf('day');

		// Disable dates that are before today or beyond the maximum selectable date
		return current && (current < today || current > maxDate);
		// return current && current <= dayjs().startOf('day') && current > booker.advance_booking_days;
	};

	/***************** END OF FUNCTIONS *****************/

	const showMessage = message => {
		return (
			<div
				style={{
					display: 'inline-block',
					border: '1px solid',
					borderColor:
						message === 'approved' ? '#5EC82A' : message === 'rejected' || message === 'cancelled' ? '#FF4D4F' : message === 'pending' ? '#FAB328' : null,
					borderRadius: '4px',
					padding: '1px 3px 1px 3px',
					justifyContent: 'space-between',
					background:
						message === 'approved' ? '#F6FFED' : message === 'rejected' || message === 'cancelled' ? '#FFF2F0' : message === 'pending' ? '#FFFBE6' : null
				}}
			>
				<span
					style={{
						color: message === 'approved'
							? '#5EC82A'
							: message === 'rejected' || message === 'cancelled'
								? '#FF4D4F'
								: message === 'pending'
									? '#FAB328'
									: null
					}}
				>
					{LabelHelper.applicationStatus(message)}
				</span>
			</div>
		);
	};

	const renderActions = ({ tripIndex, application, applicationTrip }) => {
		if (!application || !applicationTrip) {
			return null;
		}
		if (!applicationTrip.status || ['rejected', 'cancelled'].indexOf(applicationTrip.status) !== -1) {
			return null;
		}
		if (dayjs().isAfter(dayjs(applicationTrip.time_of_departure))) {
			return null;
		}
		return (
			<Row
				gutter={[32]}
				type="flex"
				justify="center"
				align="middle"
				style={{ marginLeft: '0 !important', marginRight: '0 !important' }}
			>
				<Col span={24}>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}
					>
						<Button
							danger
							style={{ marginRight: 10 }}
							loading={loadingStatus}
							onClick={() => {
								const text = 'Are you sure you want to cancel this trip?';
								if (window.confirm(text)) {
									handleCancelClick('single', applicationTrip._id);
								}
							}}
						>
							Cancel Trip
						</Button>
					</div>
				</Col>
			</Row>
		);
	};

	const handlePreviewFile = async (file) => {
		if (!file.url && !file.preview) {
			file.preview = await FileHelper.fileToDataUrl(file.originFileObj);
		}
		const newFile =  file.url || file.preview;
		setPreviewDocument(newFile);
		if (newFile.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || newFile.includes('application/pdf')) {
			setPreviewOpen(false);
		} else {
			setPreviewOpen(true);
		}
		setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf('/') + 1));
	};
	const beforeUpload = (file) => {
		const maxSize = 5 * 1024 * 1024; // 5 MB
		// Check if the file size is within the limit
		if (file.size > maxSize) {
			message.error('File size must be no more than 5MB.');
			file.status = 'error';
			return false || Upload.LIST_IGNORE;
		}
		const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
		const fileExtension = file.name.split('.').pop().toLowerCase();
		const isAllowedType = allowedTypes.includes(file.type);
		const isAllowedExtension = ['jpg', 'jpeg', 'png', 'pdf'].includes(fileExtension);
		if (!isAllowedType || !isAllowedExtension) {
			message.error('You can only upload JPG, PNG, or PDF files.');
			return false || Upload.LIST_IGNORE; // Prevents the file from being listed
		}
		// Return false to prevent automatic upload
		return false;
	};
	const initialFileList = () => {
		const list = [];
		if (application) {
			const { assets } = application;
			if (assets && assets.length > 0) {
				assets.map(asset => {
					list.push({
						uid: asset.uuid,
						name: asset.file_name,
						status: 'done',
						url: asset.file,
						type: asset.type,
						extension: asset.extension
					});
				});
			}
		}
		setFileList(list);
		initialFileListRef.current = list;
	};
	const previewAssetModal = () => {
		let typeFile = false;
		if (previewDocument.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || previewDocument.includes('application/pdf')) {
			typeFile = true;
		}
		if (typeFile) {
			const contentType = previewDocument.split(';')[0].split(':')[1];
			const byteCharacters = atob(previewDocument.substr(`data:${contentType};base64,`.length));
			const byteArrays = [];
			for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
				const slice = byteCharacters.slice(offset, offset + 1024);
				const byteNumbers = new Array(slice.length);
				for (let i = 0; i < slice.length; i++) {
					byteNumbers[i] = slice.charCodeAt(i);
				}
				const byteArray = new Uint8Array(byteNumbers);
				byteArrays.push(byteArray);
			}
			const blob = new Blob(byteArrays, { type: contentType });
			const blobUrl = URL.createObjectURL(blob);
			window.open(blobUrl, '_blank');
			setPreviewDocument('');
			setPreviewTitle('');
		} else {
			return (
				<Modal
					open={previewOpen}
					title={previewTitle}
					footer={null}
					onCancel={() =>{
						setPreviewDocument('');
						setPreviewTitle('');
						setPreviewOpen(false);
					}}
					style={{ zIndex: 1050 }}
				>
					<Row
						justify={'center'}
						style={{ height: '400px' }}
					>
						<embed
							style={{ width: '100%', height: '100%' }}
							src={previewDocument}
						/>
					</Row>
				</Modal>
			);
		}
	};

	if (loading || !application || loadingStaffs) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center' }}>
				<Loading />
			</div>
		);
	}

	const canEditApplication = ApplicationHelper.canEditApplication({ application });

	// True only when the user has actually changed an editable value in either form since
	// load. We compare normalized snapshots (editable fields only) so derived/auto-filled
	// fields such as estimated arrival times do not count as user changes.
	const buildBasicSnapshot = (values = {}) => ({
		booker_department: values.booker_department || null,
		booker_email: values.booker_email || null,
		booker_name: values.booker_name || null,
		contact_person_mobile: values.contact_person_mobile || null,
		contact_person_name: values.contact_person_name || null,
		booking_managers: [...(values.booking_managers || [])].sort(),
		date_of_trip: values.date_of_trip ? dayjs(values.date_of_trip).valueOf() : null,
		trip_country: values.trip_country || null,
		guest_staff_email: values.guest_staff_email || null,
		guest_staff_name: values.guest_staff_name || null,
		guest_staff_title: values.guest_staff_title || null,
		guest_type: values.guest_type || null,
		guest_vip_name: values.guest_vip_name || null,
		guest_vip_title: values.guest_vip_title || null,
		need_welcab: !_.isNil(values.need_welcab) ? values.need_welcab : null,
		purpose_of_trip: values.purpose_of_trip || null
	});
	const buildTripsSnapshot = (values = {}) => (values.trips || []).map(buildTripSnapshot);
	// Compare by uid only: existing documents keep their asset uuid (see initialFileList),
	// newly uploaded files get their own antd-generated uid, so this catches adds/removes.
	const buildFileListSnapshot = (list = []) => list.map(file => file.uid).sort();
	const isFormDirty =
		!_.isEqual(buildBasicSnapshot(form1.getFieldsValue(true)), buildBasicSnapshot(initialFormSnapshotRef.current.form1))
		|| !_.isEqual(buildTripsSnapshot(form2.getFieldsValue(true)), buildTripsSnapshot(initialFormSnapshotRef.current.form2))
		|| !_.isEqual(buildFileListSnapshot(fileList), buildFileListSnapshot(initialFileListRef.current));

	const confirmCancelAllTrips = () => {
		const text = 'Confirmation to cancel all application trips';
		if (window.confirm(text)) {
			handleCancelClick('all');
		}
	};

	return (
		<>
			{modalContextHolder}
			<ModalMap
				activeIndex={activeIndex}
				setActiveIndex={setActiveIndex}
				setTripRoutes={setTripRoutes}
				tripRoutes={tripRoutes}
				setOrigin={setOrigin}
				origin={origin}
				setDestination={setDestination}
				destination={destination}
				setIsModalOpen={setIsModalOpen}
				isModalOpen={isModalOpen}
				handleGetAddress={handleGetAddress}
				routeType={routeType}
				setRouteType={setRouteType}
				directions={null}
				isLoaded={isLoaded}
				windowWidth={windowWidth}
				alert={props.alert}
				handleDirections={handleDirections}
			/>
			<Form
				form={form1}
				layout="vertical"
				onFinish={() => handleSaveButtonClicked()}
			>
				<Row
					type="flex"
					justify="center"
					align="middle"
				>
					<Col
						xl={18}
						lg={18}
						md={24}
						sm={24}
						xs={24}
					>
						<span style={{ fontSize: 24, color: '#003366', fontWeight: 700 }}>Booking ID - {custom_id}</span>
					</Col>
				</Row>

				<Row
					type="flex"
					justify="center"
					style={{ marginTop: 16 }}
				>
					<Col
						xl={{ span: 8 }}
						lg={{ span: 8 }}
						md={{ span: 24 }}
						sm={24}
						xs={24}
					>
						<span style={{ fontSize: '20px' }}>Booker Information</span>
						<Descriptions
							style={{ marginTop: 16 }}
							layout="horizontal"
							column={1}
						>
							<Descriptions.Item label="Booker Name">{booker_name}</Descriptions.Item>
							<Descriptions.Item label="Booker Email">{booker_email}</Descriptions.Item>
							<Descriptions.Item label="Boooker Department">{booker_department}</Descriptions.Item>
						</Descriptions>

						<div style={{ marginTop: 12 }}>
							<span style={{ fontSize: '20px' }}>Contact Information</span>
							<Form.Item
								label="Contact Person Name"
								name="contact_person_name"
								rules={[
									{
										required: true,
										message: 'Please enter contact person name.'
									},
									{
										pattern: new RegExp(/^[A-Z a-z]+$/),
										message: 'Invalid Name'
									},
									{
										validator: (_, value) => {
											if (!value || value.trim() === '') {
												return Promise.reject('Contact Person Name cannot be empty or contain only spaces');
											}
											return Promise.resolve();
										}
									}
								]}
								style={windowWidth < 992 ? { marginTop: 48 } : { marginTop: 15 }}
							>
								<AutoComplete
									placeholder="Enter Contact Person Name"
									disabled={!canEditApplication}
									options={
										previous_bookings &&
										previous_bookings.map(booking => {
											return {
												value: booking.contact_person_name,
												label: booking.contact_person_name
											};
										})
									}
								/>
							</Form.Item>
							<Form.Item
								label="Contact Person Mobile"
								name="contact_person_mobile"
								rules={[
									{
										required: true,
										message: 'Please enter contact person name.'
									},
									{ validator: validatePhone }]}
							>
								<AutoComplete
									placeholder="Enter Contact Person Mobile"
									disabled={!canEditApplication}
									options={
										previous_bookings &&
										previous_bookings.map(booking => {
											return {
												value: booking.contact_person_mobile,
												label: booking.contact_person_mobile
											};
										})
									}
								/>
							</Form.Item>
						</div>
						<div style={{ marginTop: 36 }}>
							<span style={{ fontSize: '20px' }}>Booking Manager(s)</span>
							<div style={windowWidth < 992 ? { marginTop: 48 } : { marginTop: 15 }}>
								<Text>Delegate other colleague to manage this booking.</Text>
								<Form.List
									label="Booking Manager"
									name="booking_managers"
									initialValue={[null]}
								>
									{(fields, { add, remove }) => (
										<>
											{fields.map(({ key, name, ...restField }, index) => (
												<div
													key={key}
													style={{
														display: 'flex',
														flexDirection: 'column',
														marginBottom: -5,
														width: '100%',
														marginRight: 20
													}}
												>
													<Row gutter={16}>
														<Col span={22}>
															<Item
																name={[name]}
																{...restField}
																rules={emailRules}
															// extra="We must make sure that your are a human."
															>
																<Input
																	placeholder="Enter email address"
																	disabled={booker_id !== booker._id || !canEditApplication}
																/>
															</Item>
														</Col>
														<Col span={2}>
															<div style={{ marginTop: 4, float: 'left' }}>
																{booker_id === booker._id && canEditApplication && (
																	<CloseCircleOutlined
																		style={{ color: 'red' }}
																		onClick={() => { remove(name); }}
																	/>
																)}
															</div>
														</Col>
													</Row>
												</div>
											))}
											{fields.length < 7 && (
												<Item>
													<Button
														type="dashed"
														onClick={() => {
															add();
														}}
														block
														icon={<PlusOutlined />}
														disabled={booker_id !== booker._id || !canEditApplication}
														style={{ marginTop: 8 }}
													>
														Add Booking Manager
													</Button>
												</Item>
											)}
										</>
									)}
								</Form.List>
							</div>
						</div>
					</Col>
					<Col
						xl={{ span: 8, offset: 2 }}
						lg={{ span: 8, offset: 2 }}
						md={24}
						sm={24}
						xs={24}
						style={windowWidth < 992 ? { marginTop: 48 } : ''}
					>
						<span style={{ fontSize: '20px' }}>Guest Information</span>
						<Form.Item
							label="Guest Type"
							name="guest_type"
							rules={[{ required: true, message: 'Please select guest type.' }]}
							style={{ marginTop: 10 }}
						>
							<Select
								allowClear
								placeholder="Select Guest Type"
								disabled={!canEditApplication}
								onChange={value => {
									setSelectedGuestType(value);
									form1.setFieldsValue({
										guest_staff_email: null,
										guest_staff_name: null,
										guest_staff_title: null,
										guest_vip_name: null,
										guest_vip_title: null,
										trip_country: null
									});

									const { trips } = form2.getFieldsValue();
									const newTrips = trips && trips.length > 0 && trips.map(trip => {
										const { passengers: newPassengers } = trip;
										newPassengers[0] = null;

										return {
											...trip,
											passengers: newPassengers
										};
									});
									form2.setFieldsValue({ trips: newTrips });
								}}
							>
								{CommonEnum.guest_type.map(type => (
									<Option value={type.value}>{type.label}</Option>
								))}
							</Select>
						</Form.Item>
						{selectedGuestType === 'staff' && (
							<Form.Item
								label="Guest Staff Email Address"
								name="guest_staff_email"
								rules={selectedGuestType === 'staff' ? emailRules : []}
								help={
									<span style={{ color: '#FF0000', textDecoration: 'underline' }}>
										Restricted to managers or higher-level personnel. Email address is for vehicle arrangement only. The staff would not be notified through email.
									</span>
								}
								style={{ paddingBottom: 12 }}
							>
								<AutoComplete
									defaultOpen={false}
									placeholder="Enter Staff Email Address"
									disabled={selectedGuestType !== 'staff' || !canEditApplication}
									onSelect={handleEmailOnSelect}
									onBlur={e => handleEmailOnSelect(e.target.value)}
									options={
										previous_bookings
										&& (_.uniq(_.compact(previous_bookings.map(b => b.guest_staff_email)))).map(email => {
											return {
												value: email,
												label: email
											};
										})
									}
								/>
							</Form.Item>
						)}
						{selectedGuestType === 'staff' && (
							<Form.Item
								label="Staff Name (Auto-Complete)"
								name="guest_staff_name"
								rules={[]}
							>
								<Input
									placeholder="Staff Name (Auto-Complete)"
									disabled={true}
								/>
							</Form.Item>
						)}
						{selectedGuestType === 'staff' && (
							<Form.Item
								label="Staff Title (Auto-Complete)"
								name="guest_staff_title"
								rules={[]}
							>
								<Input
									placeholder="Enter Staff Title"
									disabled={true}
								/>
							</Form.Item>
						)}
						{selectedGuestType === 'vip' && (
							<Form.Item
								label="VIP Guest Name"
								name="guest_vip_name"
								rules={selectedGuestType === 'vip' ? vipGuestNameRules : []}
							>
								<AutoComplete
									placeholder="Enter VIP Guest Name"
									disabled={selectedGuestType !== 'vip'}
									options={
										previous_bookings &&
										previous_bookings.map(booking => {
											return {
												value: booking.guest_vip_name,
												label: booking.guest_vip_name
											};
										})
									}
									onBlur={(event) => {
										const { trips } = form2.getFieldsValue();
										const newTrips = trips && trips.length > 0 && trips.map(trip => {
											const { passengers: newPassengers } = trip;
											newPassengers[0] = event.target.value;

											return {
												...trip,
												passengers: newPassengers
											};
										});
										form2.setFieldsValue({ trips: newTrips });
									}}
								/>
							</Form.Item>
						)}
						{selectedGuestType === 'vip' && (
							<Form.Item
								label="VIP Guest Title"
								name="guest_vip_title"
								rules={selectedGuestType === 'vip' ? [{ required: true, message: 'Please input VIP Guest title.' }] : []}
							>
								<Input
									placeholder="Enter VIP Guest Title"
								/>
							</Form.Item>
						)}
						{booker && booker.is_welcab === 'yes' && (
							<Form.Item
								label="Need Welcab"
								name="need_welcab"
								rules={[]}
							>
								<Select
									placeholder="Select"
									disabled={!canEditApplication}
								>
									<Option value={true}>Yes</Option>
									<Option value={false}>No</Option>
								</Select>
							</Form.Item>
						)}
						{/* Only show when for submitted application */}
						{/* {!is_draft && (
							<Row
								type="flex"
								justify="end"
							>
								<Button
									type="primary"
									onClick={() => handleSaveButtonClicked()}
									loading={loadingSubmit}
									disabled={!canEditApplication}
								>
									Update and Save
								</Button>
							</Row>
						)} */}
						<Form.Item
							name="assets"
							className="upload-card"
							label="Supporting Documents (Max 5MB)"
						>
							<Dragger
								beforeUpload={beforeUpload}
								name="file"
								multiple={true}
								action={null}
								onPreview={handlePreviewFile}
								onChange={(info) => {
									const { fileList } = info || [];
									const processFileList = async () => {
										const modifiedList = [];
										for (let i=0; i<fileList.length; i++) {
											const list = fileList[i];
											modifiedList.push({
												...list,
												...!list.url && { url: await FileHelper.fileToDataUrl(info.file) }
											});
										}
										setFileList(modifiedList);
									};
									processFileList();
								}}
								fileList={fileList}
								showUploadList={{ showRemoveIcon: true }}
								maxCount={5}
								disabled={!canEditApplication}
							>
								<p style={{ margin: 0 }}>
									<FontAwesomeIcon
										icon={faImages}
										style={{ color: '#000000', fontSize: '2em', marginTop: '26px', marginBottom: '9px' }}
									/>
								</p>
								<p style={{ margin: 0, fontSize: 14 }}>Click or drag file to this area to upload</p>
								<p style={{ margin: 0, marginBottom: '27px', fontSize: 10, color: '#6B6B6B' }}>
									Supported files: JPEG, PNG, PDF (Maximum of 5 uploads with 5MB each)
								</p>
							</Dragger>
						</Form.Item>
					</Col>
				</Row>
				<Row
					type="flex"
					justify="center"
					align="middle"
				>
					<Col
						xl={18}
						lg={18}
						md={24}
						sm={24}
						xs={24}
					>
						<Divider dashed />
					</Col>
				</Row>

				{/* Trip details */}
				<Row
					type="flex"
					style={{ marginTop: 8 }}
				>
					<Col
						xl={{ offset: 3, span: 8 }}
						lg={{ offset: 3, span: 8 }}
						md={{ span: 24 }}
						sm={24}
						xs={24}
					>
						<span style={{ fontSize: '20px' }}>Trip Details (10 Trips Max.)</span>
					</Col>
				</Row>
				<Row
					type="flex"
					style={{ marginTop: 8 }}
				>
					<Col
						xl={{ offset: 3, span: 8 }}
						lg={{ offset: 3, span: 8 }}
						md={{ span: 24 }}
						sm={24}
						xs={24}
					>
						<Form.Item
							label="Date of Trip"
							name="date_of_trip"
							rules={[{ required: true, message: 'Please select date of trip' }]}
							style={{ marginTop: 10 }}
						>
							<DatePicker
								format="YYYY-MM-DD"
								style={{ width: '100%' }}
								allowClear={false}
								disabled={!canEditApplication}
								onChange={value => {
									setDateOfTrip(value);
									handleDirections(tripRoutes);
								}}
							/>
						</Form.Item>

						{canBookChinaTrip && (
							<Form.Item
								name="trip_country"
								label="Hong Kong Trip / China Trip"
								rules={[{ required: true, message: 'Please select date of trip' }]}
								style={{ marginTop: 10 }}
							>
								<Select
									placeholder="Select"
									disabled={!canEditApplication}
								>
									<Option value="hongkong">Hong Kong Trip</Option>
									<Option value="china">Hong Kong - China Trip</Option>
								</Select>
							</Form.Item>
						)}

						<Form.Item
							name="purpose_of_trip"
							label="Purpose of Trip"
							rules={[{ required: true, message: 'Please purpose of trip' }]}
						>
							<Select
								showSearch
								placeholder="Select"
								disabled={!canEditApplication}
								onChange={(value) => {
									setSelectedPurposeOfTrip(value);
								}}
							>
								<Option value="business">Business</Option>
								<Option value="local_pickup_deliver">Local Pickup / Deliver</Option>
								<Option value="airport_pickup_deliver">Airport Pickup / Deliver</Option>
								{/* <Option value="other">Other</Option> */}
							</Select>
						</Form.Item>
						{
							selectedPurposeOfTrip === 'airport_pickup_deliver' && (
								<Form.Item>
									<Text
										type="secondary"
										style={{ display: 'block', marginTop: 8, color: '#FF0000', textDecoration: 'underline' }}
									>
										Note: Approval is required from the Office of the President or Vice-President. Please upload the approval on this form for supporting documents.
									</Text>
								</Form.Item>
							)
						}
					</Col>
				</Row>
			</Form>

			{/* Using form List in order to add new trips */}
			<Form
				form={form2}
				layout="vertical"
			>
				<Form.List
					name="trips"
					initialValue={[null]}
				>
					{(trips, { add, remove }) => (
						<>
							{trips.map(({ key, name: tripName, ...restField }, tripIndex) => {
								const { trips } = form2.getFieldsValue();
								const canEditApplicationTrip = ApplicationHelper.canEditApplicationTrip({ applicationTrip: trips ? trips[tripIndex] : null });
								return (
									<>
										<Row
											type="flex"
											justify="center"
											align="middle"
											style={{ marginTop: 16 }}
											key={key}
										>
											<Col
												xl={18}
												lg={18}
												md={24}
												sm={24}
												xs={24}
											>
												<Card
													actions={[
														trips && trips.length > 0
															? renderActions({
																tripIndex,
																application,
																applicationTrip: trips[tripIndex]
															})
															: []
													]}
													extra={
														(
															tripIndex > 0 && trips && trips.length > 0 && trips[tripIndex] && trips[tripIndex]._id === undefined) && (
															<Button
																icon={<CloseOutlined />}
																disabled={!canEditApplicationTrip}
																onClick={() => {
																	const newTripRoutes = tripRoutes.filter((_, idx) => idx !== tripIndex);
																	setTripRoutes(newTripRoutes);
																	remove(tripName);
																}}
															></Button>
														)
													}
												>
													<Row
														style={{ marginTop: 16 }}
														gutter={[32]}
													>
														<Col
															xl={12}
															lg={12}
															md={24}
															sm={24}
															xs={24}
														>
															<div>
																<span style={{ fontSize: '20px', marginRight: 16 }}>
																	<u>Trip {tripIndex ? tripIndex + 1 : 1}</u>
																</span>
																{application.trips && application.trips.length > 0 && application.trips[tripIndex] && (
																	<span>
																		{
																			showMessage(application.trips[tripIndex].status
																				? (
																					application.trips[tripIndex].status === 'draft'
																						? 'pending'
																						: application.trips[tripIndex].status
																				)
																				: 'pending'
																			)
																		}
																	</span>
																)}
															</div>
														</Col>
													</Row>
													<Row
														type="flex"
														gutter={[32]}
														style={{ marginTop: 16 }}
													>
														<Col
															xl={{ span: 12 }}
															lg={{ span: 12 }}
															md={{ span: 24 }}
															sm={24}
															xs={24}
														>
															<Form.Item
																label="Time of departure"
																initialValue={null}
																name={[tripName, 'time_of_departure']}
																rules={[{ required: true, message: 'Please select time' }]}
															>
																<TimePicker
																	format={'HH:mm'}
																	placeholder="Select time"
																	disabled={!canEditApplicationTrip}
																	disabledTime={() => ({
																		disabledHours: () => Array.from(Array(6).keys()),
																		disabledMinutes: () => []
																	})}
																	style={{ width: '100%' }}
																	showNow={dateOfTrip && dayjs(dateOfTrip).format('YYYY-MM-DD') !== dayjs().format('YYYY-MM-DD')}
																	onChange={() => {
																		handleDirections(tripRoutes);
																	}}
																/>
															</Form.Item>
															<div style={{ marginBottom: 12 }}>
																<Text>
																	<span style={{ color: 'red' }}>*</span> Starting Point{' '}
																	{(selectedPickupType[tripIndex] === 'inside_hkust' || selectedPickupType[tripIndex] === 'favorite_destination') && '(Max of 6)'}
																</Text>

																<Form.Item
																	name={[tripName, 'starting_pickup_type']}
																	{...restField}
																	style={{ marginBottom: 0 }}
																	initialValue={'search_location'}
																>
																	<Radio.Group
																		disabled={!canEditApplicationTrip}
																		onChange={e => {
																			if (e.target.value === 'inside_hkust') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_pickup_address: null,
																							pinned_pickup_location_name: null,
																							pickup_address_routes_hkust: [null],
																							pickup_address_routes_favorite: []
																						}
																					}
																				});
																			}

																			if (e.target.value === 'favorite_destination') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_pickup_address: null,
																							pinned_pickup_location_name: null,
																							pickup_address_routes_hkust: [],
																							pickup_address_routes_favorite: [null]
																						}
																					}
																				});
																			}

																			if (e.target.value === 'search_location') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_pickup_address: null,
																							pinned_pickup_location_name: null,
																							pickup_address_routes_hkust: [],
																							pickup_address_routes_favorite: []
																						}
																					}
																				});
																			}

																			//	new approach instead of using 2 states
																			setSelectedPickupType(prevSelectedPickupType => {
																				const newSelectedPickupType = [...prevSelectedPickupType];
																				newSelectedPickupType[tripIndex] = e.target.value;
																				return newSelectedPickupType;
																			});

																			setTripRoutes(prevTripRoutes => {
																				const newTripRoutes = prevTripRoutes;

																				newTripRoutes[tripIndex] = {
																					...newTripRoutes[tripIndex],
																					origin: [],
																					directions: null
																				};

																				return newTripRoutes;
																			});
																		}}
																	>
																		<Radio value="search_location">Search Location</Radio>
																		<Radio value="favorite_destination">Favorite Location</Radio>
																		<Radio value="inside_hkust">Inside HKUST</Radio>
																	</Radio.Group>
																</Form.Item>
															</div>

															{/* Display select map */}
															{selectedPickupType[tripIndex] === 'search_location' && (
																<div style={{ display: 'flex', flexDirection: 'column' }}>
																	<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
																		<Form.Item
																			name={[tripName, 'pinned_pickup_location_name']}
																			rules={[{ required: true, message: 'Location Name of starting point is required.' }]}
																			{...restField}
																			style={{ width: '100%', display: 'none' }}
																		>
																			<Input
																				placeholder="Location Name of Starting Point"
																				disabled={true}
																				style={{ flex: 1 }}
																			/>
																		</Form.Item>
																		<Button
																			disabled={!canEditApplicationTrip}
																			style={{ flex: 1 }}
																			onClick={() => {
																				setActiveIndex(tripIndex);
																				setRouteType('origin');
																				setIsModalOpen(true);
																			}}
																		>
																			Select From Map
																		</Button>
																		<Tooltip
																			placement="right"
																			title="Add to Favorite Location"
																		>
																			<Button
																				icon={<HeartOutlined />}
																				disabled={
																					!canEditApplicationTrip
																					|| !is_draft
																					|| (trips && trips[tripIndex] && !trips[tripIndex].pinned_pickup_address)
																				}
																				style={{ flexShrink: 0 }}
																				onClick={() => {
																					props.createFavoriteLocation({
																						name: trips[tripIndex].pinned_pickup_address,
																						lng: trips[tripIndex].origin.lng,
																						lat: trips[tripIndex].origin.lat
																					});
																				}}
																			/>
																		</Tooltip>
																	</div>
																	<div style={{ display: 'flex' }}>
																		<Form.Item
																			name={[tripName, 'pinned_pickup_address']}
																			rules={[{ required: true, message: 'Full address of starting point is required.' }]}
																			{...restField}
																			style={{ width: '100%' }}
																		>
																			<TextArea
																				placeholder="Full address of starting point"
																				disabled={true}
																				rows={3}
																				style={{ width: '100%' }}
																			/>
																		</Form.Item>
																	</div>

																</div>
															)}
															{/* display inside hkust options */}
															{selectedPickupType[tripIndex] === 'inside_hkust' && (
																<Form.List
																	name={[tripName, 'pickup_address_routes_hkust']}
																	initialValue={[null]}
																	{...restField}
																>
																	{(fields, { add, remove }) => (
																		<>
																			{fields.map(({ key, name, ...restField }, index) => (
																				<div
																					key={key}
																					style={{
																						display: 'flex',
																						flexDirection: 'column',
																						marginBottom: -5,
																						width: '100%',
																						marginRight: 20
																					}}
																				>
																					<Row gutter={16}>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							<div
																								style={{
																									width: '20px',
																									height: '20px',
																									borderRadius: '50%',
																									backgroundColor: '#003366',
																									display: 'flex',
																									alignItems: 'center',
																									justifyContent: 'center',
																									fontSize: '12px',
																									marginTop: '4px',
																									float: 'right'
																								}}
																							>
																								<span style={{ color: '#FFFFFF' }}>{index + 1}</span>
																							</div>
																						</Col>
																						<Col span={20}>
																							<Item
																								name={[name]}
																								{...restField}
																								rules={[{ required: true, message: 'Please select pick-up point.' }]}
																							>
																								<Select
																									showSearch
																									optionFilterProp="children"
																									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
																									disabled={!canEditApplicationTrip}
																									placeholder="Select Pick-up Point"
																									onChange={value => {
																										if (value) {
																											const selectedHkustPoint = hkust_points.find(point => point._id === value);
																											const newOrigin = [...tripRoutes[tripIndex].origin];
																											const updatedPoint = {
																												lat: parseFloat(selectedHkustPoint.latitude),
																												lng: parseFloat(selectedHkustPoint.longitude)
																											};
																											newOrigin[index] = updatedPoint;
																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};
																											handleDirections(newTripRoutes);
																										} else {
																											const newOrigin = [...tripRoutes[tripIndex].origin];
																											newOrigin[index] = null; //	set to null when cleared
																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};
																											handleDirections(newTripRoutes);
																										}
																									}}
																								>
																									{hkust_points.length > 0 &&
																										hkust_points.map((hkust_point, index) => (
																											<Option
																												key={index}
																												value={hkust_point._id}
																												disabled={
																													tripRoutes[tripIndex] &&
																													tripRoutes[tripIndex].destination &&
																													tripRoutes[tripIndex].destination.filter(destination => {
																														const { longitude, latitude } = hkust_point;
																														return destination && destination.lng === parseFloat(longitude) && destination.lat === parseFloat(latitude);
																													}).length > 0
																												}
																											>
																												{hkust_point.is_popular ? `⭐ ${hkust_point.name}` : hkust_point.name}
																											</Option>
																										))}
																								</Select>
																							</Item>
																						</Col>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							{index > 0 && (
																								<div style={{ marginTop: 4, float: 'left' }}>
																									<CloseCircleOutlined
																										disabled={!canEditApplicationTrip}
																										style={{ color: 'red' }}
																										onClick={() => {
																											const newOrigin = tripRoutes[tripIndex].origin.filter((_, idx) => idx !== index);
																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};
																											handleDirections(newTripRoutes);
																											remove(name);
																										}}
																									/>
																								</div>
																							)}
																						</Col>
																					</Row>
																				</div>
																			))}
																			{fields.length < 6 && (
																				<Item>
																					<Button
																						disabled={!canEditApplicationTrip}
																						type="dashed"
																						onClick={() => {
																							add();
																							const newTripRoutes = tripRoutes;
																							newTripRoutes[tripIndex] = {
																								...newTripRoutes[tripIndex],
																								origin: [...newTripRoutes[tripIndex].origin, null]
																							};

																							handleDirections(newTripRoutes);
																						}}
																						block
																						icon={<PlusOutlined />}
																					>
																						Point (inside HKUST only)
																					</Button>
																				</Item>
																			)}
																		</>
																	)}
																</Form.List>
															)}
															{/* Display Favorite Destination / Location Options */}
															{selectedPickupType[tripIndex] === 'favorite_destination' && (
																<Form.List
																	name={[tripName, 'pickup_address_routes_favorite']}
																	initialValue={[null]}
																	{...restField}
																>
																	{(fields, { add, remove }) => (
																		<>
																			{fields.map(({ key, name, ...restField }, index) => (
																				<div
																					key={key}
																					style={{
																						display: 'flex',
																						flexDirection: 'column',
																						marginBottom: -5,
																						width: '100%',
																						marginRight: 20
																					}}
																				>
																					<Row gutter={16}>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							<div
																								style={{
																									width: '20px',
																									height: '20px',
																									borderRadius: '50%',
																									backgroundColor: '#003366',
																									display: 'flex',
																									alignItems: 'center',
																									justifyContent: 'center',
																									fontSize: '12px',
																									marginTop: '4px',
																									float: 'right'
																								}}
																							>
																								<span style={{ color: '#FFFFFF' }}>{index + 1}</span>
																							</div>
																						</Col>
																						<Col span={20}>
																							<Item
																								name={[name]}
																								{...restField}
																								rules={[{ required: true, message: 'Please select pick-up point.' }]}
																							>
																								<Select
																									showSearch
																									optionFilterProp="children"
																									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
																									disabled={!canEditApplicationTrip}
																									placeholder="Select Pick-up Point"
																									onChange={value => {
																										if (value) {
																											const selectedFavoriteDestination = favorite_destinations.find(favorite_destination => favorite_destination._id === value);
																											const newOrigin = [...tripRoutes[tripIndex].origin];
																											const updatedPoint = {
																												lat: parseFloat(selectedFavoriteDestination.latitude),
																												lng: parseFloat(selectedFavoriteDestination.longitude)
																											};
																											newOrigin[index] = updatedPoint;
																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};
																											handleDirections(newTripRoutes);
																										} else {
																											const newOrigin = [...tripRoutes[tripIndex].origin];
																											newOrigin[index] = null; //	set to null when cleared
																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};
																											handleDirections(newTripRoutes);
																										}
																									}}
																								>
																									{favorite_destinations.length > 0 &&
																										favorite_destinations.map((favorite_destination, index) => (
																											<Option
																												key={index}
																												value={favorite_destination._id}
																											>
																												{favorite_destination.name}
																											</Option>
																										))}
																								</Select>
																							</Item>
																						</Col>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							{index > 0 && (
																								<div style={{ marginTop: 4, float: 'left' }}>
																									<CloseCircleOutlined
																										disabled={!canEditApplicationTrip}
																										style={{ color: 'red' }}
																										onClick={() => {
																											const newOrigin = tripRoutes[tripIndex].origin.filter((_, idx) => idx !== index);

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												origin: newOrigin
																											};

																											handleDirections(newTripRoutes);

																											remove(name);
																										}}
																									/>
																								</div>
																							)}
																						</Col>
																					</Row>
																				</div>
																			))}
																			{fields.length < 6 && (
																				<Item>
																					<Button
																						disabled={!canEditApplicationTrip}
																						type="dashed"
																						onClick={() => {
																							add();
																							const newTripRoutes = tripRoutes;
																							newTripRoutes[tripIndex] = {
																								...newTripRoutes[tripIndex],
																								origin: [...newTripRoutes[tripIndex].origin, null]
																							};

																							handleDirections(newTripRoutes);
																						}}
																						block
																						icon={<PlusOutlined />}
																					>
																						Add Pick-up Point
																					</Button>
																				</Item>
																			)}
																		</>
																	)}
																</Form.List>
															)}

															<div style={{ marginBottom: 12 }}>
																<Text>
																	<span style={{ color: 'red' }}>*</span> Destination
																</Text>

																<Form.Item
																	name={[tripName, 'drop_off_type']}
																	{...restField}
																	style={{ marginBottom: 0 }}
																	initialValue={'search_location'}
																>
																	<Radio.Group
																		disabled={!canEditApplicationTrip}
																		onChange={e => {
																			if (e.target.value === 'inside_hkust') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_drop_off_address: null,
																							pinned_drop_off_location_name: null,
																							dropoff_address_routes_hkust: [null],
																							dropoff_address_routes_favorite: []
																						}
																					}
																				});
																			}

																			if (e.target.value === 'favorite_destination') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_drop_off_address: null,
																							pinned_drop_off_location_name: null,
																							dropoff_address_routes_hkust: [],
																							dropoff_address_routes_favorite: [null]
																						}
																					}
																				});
																			}

																			if (e.target.value === 'search_location') {
																				form2.setFieldsValue({
																					trips: {
																						[tripIndex]: {
																							pinned_drop_off_address: null,
																							pinned_drop_off_location_name: null,
																							dropoff_address_routes_hkust: [],
																							dropoff_address_routes_favorite: []
																						}
																					}
																				});
																			}

																			//	new approach
																			setSelectedDropoffType(prevSelectedDropoffType => {
																				const newSelectedDropoffType = [...prevSelectedDropoffType];
																				newSelectedDropoffType[tripIndex] = e.target.value;
																				return newSelectedDropoffType;
																			});

																			setTripRoutes(prevTripRoutes => {
																				const newTripRoutes = prevTripRoutes;

																				newTripRoutes[tripIndex] = {
																					...newTripRoutes[tripIndex],
																					destination: [],
																					directions: null
																				};

																				return newTripRoutes;
																			});
																		}}
																	>
																		<Radio value="search_location">Search Location</Radio>
																		<Radio value="favorite_destination">Favorite Location</Radio>
																		<Radio value="inside_hkust">Inside HKUST</Radio>
																	</Radio.Group>
																</Form.Item>
															</div>

															{/* Display select map */}
															{selectedDropoffType[tripIndex] === 'search_location' && (
																<div style={{ display: 'flex', flexDirection: 'column' }}>
																	<div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
																		<Form.Item
																			name={[tripName, 'pinned_drop_off_location_name']}
																			rules={[{ required: true, message: 'Location Name of destination is required.' }]}
																			{...restField}
																			style={{ width: '100%', display: 'none' }}
																		>
																			<Input
																				placeholder="Location Name of Destination"
																				disabled={true}
																				style={{ flex: 1 }}
																			/>
																		</Form.Item>
																		<Button
																			disabled={false}
																			style={{ flex: 1 }}
																			onClick={() => {
																				setActiveIndex(tripIndex);
																				setRouteType('destination');
																				setIsModalOpen(true);
																			}}
																		>
																			Select From Map
																		</Button>
																		<Tooltip
																			placement="right"
																			title="Add to Favorite Location"
																		>
																			<Button
																				icon={<HeartOutlined />}
																				disabled={
																					!canEditApplicationTrip
																					|| (!is_draft)
																					|| (trips && trips[tripIndex] && !trips[tripIndex].pinned_drop_off_address)
																				}
																				style={{ flexShrink: 0 }}
																				onClick={() => {
																					props.createFavoriteLocation({
																						name: trips[tripIndex].pinned_drop_off_address,
																						lng: trips[tripIndex].destination.lng,
																						lat: trips[tripIndex].destination.lat
																					});
																				}}
																			/>
																		</Tooltip>
																	</div>
																	<div style={{ display: 'flex' }}>
																		<Form.Item
																			name={[tripName, 'pinned_drop_off_address']}
																			rules={[{ required: true, message: 'Full address of destination is required.' }]}
																			{...restField}
																			style={{ width: '100%' }}
																		>
																			<TextArea
																				placeholder="Full address of destination"
																				disabled={true}
																				rows={3}
																				style={{ width: '100%' }}
																			/>
																		</Form.Item>
																	</div>
																</div>

															)}

															{/* Display Inside Hkust Options  */}
															{selectedDropoffType[tripIndex] === 'inside_hkust' && (
																<Form.List
																	name={[tripName, 'dropoff_address_routes_hkust']}
																	initialValue={[null]}
																	{...restField}
																>
																	{(fields, { add, remove }) => (
																		<>
																			{fields.map(({ key, name, ...restField }, index) => (
																				<div
																					key={key}
																					style={{
																						display: 'flex',
																						flexDirection: 'column',
																						marginBottom: -5,
																						width: '100%',
																						marginRight: 20
																					}}
																				>
																					<Row gutter={16}>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							<div
																								style={{
																									width: '20px',
																									height: '20px',
																									borderRadius: '50%',
																									backgroundColor: '#003366',
																									display: 'flex',
																									alignItems: 'center',
																									justifyContent: 'center',
																									fontSize: '12px',
																									marginTop: '4px',
																									float: 'right'
																								}}
																							>
																								<span style={{ color: '#FFFFFF' }}>{index + 1}</span>
																							</div>
																						</Col>
																						<Col span={20}>
																							<Item
																								name={[name]}
																								{...restField}
																								rules={[{ required: true, message: 'Please select drop-off point.' }]}
																							>
																								<Select
																									showSearch
																									optionFilterProp="children"
																									disabled={!canEditApplicationTrip}
																									placeholder="Select Drop-off Point"
																									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
																									onChange={value => {
																										if (value) {
																											const selectedHkustPoint = hkust_points.find(point => point._id === value);
																											const newDestination = [...tripRoutes[tripIndex].destination];
																											const updatedPoint = {
																												lat: parseFloat(selectedHkustPoint.latitude),
																												lng: parseFloat(selectedHkustPoint.longitude)
																											};
																											newDestination[index] = updatedPoint;

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);
																										} else {
																											const newDestination = [...tripRoutes[tripIndex].destination];
																											newDestination[index] = null; //	set to null when cleared

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);
																										}
																									}}
																								>
																									{hkust_points.length > 0 &&
																										hkust_points.map((hkust_point, index) => (
																											<Option
																												key={index}
																												value={hkust_point._id}
																												disabled={
																													tripRoutes[tripIndex] &&
																													tripRoutes[tripIndex].origin &&
																													tripRoutes[tripIndex].origin.filter(origin => {
																														const { longitude, latitude } = hkust_point;
																														return origin && origin.lng === parseFloat(longitude) && origin.lat === parseFloat(latitude);
																													}).length > 0
																												}
																											>
																												{hkust_point.is_popular ? `⭐ ${hkust_point.name}` : hkust_point.name}
																											</Option>
																										))
																									}
																								</Select>
																							</Item>
																						</Col>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							{index > 0 && (
																								<div style={{ marginTop: 4, float: 'left' }}>
																									<CloseCircleOutlined
																										disabled={!canEditApplicationTrip}
																										style={{ color: 'red' }}
																										onClick={() => {
																											const newDestination = tripRoutes[tripIndex].destination.filter((_, idx) => idx !== index);

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);

																											remove(name);
																										}}
																									/>
																								</div>
																							)}
																						</Col>
																					</Row>
																				</div>
																			))}
																			{fields.length < 6 && (
																				<Item>
																					<Button
																						disabled={!canEditApplicationTrip}
																						type="dashed"
																						onClick={() => {
																							add();
																							const newTripRoutes = tripRoutes;
																							newTripRoutes[tripIndex] = {
																								...newTripRoutes[tripIndex],
																								destination: [...newTripRoutes[tripIndex].destination, null]
																							};

																							handleDirections(newTripRoutes);
																						}}
																						block
																						icon={<PlusOutlined />}
																					>
																						Point (inside HKUST only)
																					</Button>
																				</Item>
																			)}
																		</>
																	)}
																</Form.List>
															)}

															{/* Display Favorite Destination / Location Options */}
															{selectedDropoffType[tripIndex] === 'favorite_destination' && (
																<Form.List
																	name={[tripName, 'dropoff_address_routes_favorite']}
																	initialValue={[null]}
																	{...restField}
																// rules={[{ required: true, message: 'Please select drop-off point.' }]}
																>
																	{(fields, { add, remove }) => (
																		<>
																			{fields.map(({ key, name, ...restField }, index) => (
																				<div
																					key={key}
																					style={{
																						display: 'flex',
																						flexDirection: 'column',
																						marginBottom: -5,
																						width: '100%',
																						marginRight: 20
																					}}
																				>
																					<Row gutter={16}>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							<div
																								style={{
																									width: '20px',
																									height: '20px',
																									borderRadius: '50%',
																									backgroundColor: '#003366',
																									display: 'flex',
																									alignItems: 'center',
																									justifyContent: 'center',
																									fontSize: '12px',
																									marginTop: '4px',
																									float: 'right'
																								}}
																							>
																								<span style={{ color: '#FFFFFF' }}>{index + 1}</span>
																							</div>
																						</Col>
																						<Col span={20}>
																							<Item
																								name={[name]}
																								{...restField}
																								rules={[{ required: true, message: 'Please select drop-off point.' }]}
																							>
																								<Select
																									showSearch
																									optionFilterProp="children"
																									filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
																									disabled={!canEditApplicationTrip}
																									placeholder="Select Drop-off Point"
																									onChange={value => {
																										if (value) {
																											const selectedFavoriteDestination = favorite_destinations.find(point => point._id === value);
																											const newDestination = [...tripRoutes[tripIndex].destination];
																											const updatedPoint = {
																												lat: parseFloat(selectedFavoriteDestination.latitude),
																												lng: parseFloat(selectedFavoriteDestination.longitude)
																											};
																											newDestination[index] = updatedPoint;

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);
																										} else {
																											const newDestination = [...tripRoutes[tripIndex].destination];
																											newDestination[index] = null; //	set to null when cleared

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);
																										}
																									}}
																								>
																									{favorite_destinations.length > 0 &&
																										favorite_destinations.map((favorite_destination, index) => (
																											<Option
																												key={index}
																												value={favorite_destination._id}
																											>
																												{favorite_destination.name}
																											</Option>
																										))
																									}
																								</Select>
																							</Item>
																						</Col>
																						<Col
																							span={2}
																							style={{ position: 'relative' }}
																						>
																							{index > 0 && (
																								<div style={{ marginTop: 4, float: 'left' }}>
																									<CloseCircleOutlined
																										disabled={!canEditApplicationTrip}
																										style={{ color: 'red' }}
																										onClick={() => {
																											const newDestination = tripRoutes[tripIndex].destination.filter((_, idx) => idx !== index);

																											const newTripRoutes = tripRoutes;
																											newTripRoutes[tripIndex] = {
																												...newTripRoutes[tripIndex],
																												destination: newDestination
																											};

																											handleDirections(newTripRoutes);

																											remove(name);
																										}}
																									/>
																								</div>
																							)}
																						</Col>
																					</Row>
																				</div>
																			))}
																			{fields.length < 6 && (
																				<Item>
																					<Button
																						disabled={!canEditApplicationTrip}
																						type="dashed"
																						onClick={() => {
																							add();
																							const newTripRoutes = tripRoutes;
																							newTripRoutes[tripIndex] = {
																								...newTripRoutes[tripIndex],
																								destination: [...newTripRoutes[tripIndex].destination, null]
																							};

																							handleDirections(newTripRoutes);
																						}}
																						block
																						icon={<PlusOutlined />}
																					>
																						Add Drop-off Point
																					</Button>
																				</Item>
																			)}
																		</>
																	)}
																</Form.List>
															)}

															<Text>
																<span style={{ color: 'red' }}>*</span> Passenger(s) (Max of 7)
															</Text>
															<Form.List
																name={[tripName, 'passengers']}
																initialValue={[null]}
															>
																{(fields, { add, remove }) => (
																	<>
																		{fields.map(({ key, name, ...restField }, index) => (
																			<div
																				key={key}
																				style={{
																					display: 'flex',
																					flexDirection: 'column',
																					marginBottom: -5,
																					width: '100%',
																					marginRight: 20
																				}}
																			>
																				<Row gutter={16}>
																					<Col span={22}>
																						<Item
																							name={[name]}
																							{...restField}
																							rules={[
																								{ required: true, message: 'Please enter passenger name.' },
																								{
																									validator: (_, value) => {
																										if (!value || value.trim() === '') {
																											return Promise.reject('Passenger Name cannot be empty or contain only spaces');
																										}
																										return Promise.resolve();
																									}
																								}
																							]}
																						>
																							<Input
																								disabled={!canEditApplicationTrip}
																								placeholder="Enter Passenger Name"
																							/>
																						</Item>
																					</Col>
																					<Col span={2}>
																						{
																							index > 0 && (
																								<div style={{ marginTop: 4, float: 'left' }}>
																									<CloseCircleOutlined
																										disabled={!canEditApplicationTrip}
																										style={{ color: 'red' }}
																										onClick={() => {
																											remove(name);
																										}}
																									/>
																								</div>
																							)
																						}
																					</Col>
																				</Row>
																			</div>
																		))}
																		{fields.length < 7 && (
																			<Item>
																				<Button
																					disabled={!canEditApplicationTrip}
																					type="dashed"
																					onClick={() => {
																						add();
																					}}
																					block
																					icon={<PlusOutlined />}
																				>
																					Add Passenger
																				</Button>
																			</Item>
																		)}
																	</>
																)}
															</Form.List>
															<Form.Item
																name={[tripName, 'remark']}
																label="Remarks"
																rules={
																	selectedPurposeOfTrip === 'other'
																		? [{ required: true, message: 'Please enter remark.' }]
																		: []
																}
																style={{ marginTop: 10 }}
															>
																<TextArea
																	placeholder="Enter Remarks"
																	disabled={!canEditApplicationTrip}
																/>
															</Form.Item>
														</Col>

														<Col
															xl={{ span: 12 }}
															lg={{ span: 12 }}
															md={{ span: 24 }}
															sm={24}
															xs={24}
															style={windowWidth < 992 ? { marginTop: 48 } : ''}
														>
															{tripRoutes[tripIndex] && tripRoutes[tripIndex].hasError ? (
																<Empty />
															) : isLoaded && tripRoutes[tripIndex] && !_.isNil(tripRoutes[tripIndex].directions) && !isModalOpen ? (
																<GoogleMap
																	mapContainerStyle={{ width: '100%', height: '400px' }}
																	zoom={10}
																>
																	<>
																		<DirectionsRenderer directions={tripRoutes[tripIndex].directions} />
																		{tripRoutes[tripIndex].infoWindows.length > 0 &&
																			tripRoutes[tripIndex].infoWindows.map((info, index) => (
																				<InfoWindow position={info.position}>
																					<div>
																						<CarOutlined style={{ marginRight: '5px', fontWeight: 700 }} />
																						<span>eta: </span>
																						<span style={{ fontWeight: 700 }}>{info.trafficDuration}</span>
																						<br />
																						<span>{info.distance}</span>
																					</div>
																				</InfoWindow>
																			))}
																	</>
																</GoogleMap>
															) : isModalOpen ? (
																<div style={{ display: 'flex', justifyContent: 'center' }}>
																	<Loading />
																</div>
															) : (
																<></>
															)}
														</Col>
													</Row>
												</Card>
											</Col>
										</Row>
									</>
								);
							})}
							{trips.length < 10 && (
								<Row
									type="flex"
									justify="center"
									align="middle"
									style={{ marginTop: 16 }}
								>
									<Col
										xl={18}
										lg={18}
										md={24}
										sm={24}
										xs={24}
									>
										<Card>
											<Button
												icon={<PlusOutlined />}
												disabled={!canEditApplication}
												onClick={() => {
													setTripRoutes(prevTripRoutes => [
														...prevTripRoutes,
														{
															origin: [],
															destination: [],
															directions: null,
															hasError: false
														}
													]);
													setSelectedPickupType(prevSelectedPickupType => [...prevSelectedPickupType, 'search_location']);
													setSelectedDropoffType(prevSelectedDropoffType => [...prevSelectedDropoffType, 'search_location']);
													const { trips } = form2.getFieldsValue();
													const latestTrip = trips[trips.length - 1];
													const newPassengers = latestTrip ? { passengers: latestTrip.passengers } : {};
													add(newPassengers);
												}}
											>
												Add new Trip
											</Button>
										</Card>
									</Col>
								</Row>
							)}
						</>
					)}
				</Form.List>
			</Form>

			<Row
				type="flex"
				justify="center"
				align="middle"
				style={{ marginTop: 16 }}
			>
				<Col
					xl={18}
					lg={18}
					md={24}
					sm={24}
					xs={24}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: application && application.trips && application.trips.length > 0 && application.trips.some(trip => trip.status === 'pending' || trip.status === 'approved')
								? 'space-between'
								: 'flex-end',
							// float: 'right'
							width: '100%'
						}}
					>
						{/* <div>
							<Button
								onClick={() => props.history.push('/applications')}
							>
									Return to Booking List
							</Button>
						</div> */}
						{
							application && application.trips && application.trips.length > 0 && application.trips.some(trip => trip.status === 'pending' || trip.status === 'approved') && (
								<div>
									<Button
										loading={loadingCancel}
										onClick={() => confirmCancelAllTrips()}
										color="red"
										style={{
											marginRight: 10,
											border: '1px solid #FF4D4F',
											color: '#FF4D4F'
										}}
										disabled={!canEditApplication}
									>
									Cancel All Trips
									</Button>
								</div>
							)
						}
						<Button
							type="primary"
							onClick={() => handleSaveButtonClicked()}
							loading={loadingSubmit}
							disabled={!canEditApplication || !isFormDirty}
						>
							Update and Save
						</Button>
					</div>
				</Col>
			</Row>
			{previewAssetModal()}
		</>
	);
};

export default compose(withAlert())(BookingApplicationView);
